import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "creatoraiq_session_token";

// True inside a Capacitor mobile shell (iOS / Android). Web is unaffected.
// Injected globally by Capacitor before the JS bundle runs.
const IS_NATIVE = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();

/**
 * Session-token helpers for Capacitor / mobile.
 *
 * The web build relies on httpOnly cookies set by the backend. Capacitor
 * WKWebView blocks third-party cookies aggressively (ITP), so cross-origin
 * cookies from `capacitor://localhost` to `linkola.app` are not sent on
 * subsequent requests — the user appears "logged out" on every relaunch.
 *
 * As a fallback, the backend now echoes the session token in login response
 * bodies. We persist that token in localStorage and attach it via
 * `Authorization: Bearer <token>` on every API request. The backend accepts
 * both cookie *and* Bearer forms (`deps.py::_get_session_token`), so this
 * layer is a no-op on web where cookies work.
 */
export function setSessionToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* private mode / disabled storage — silently ignore */ }
}

export function getSessionToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ""; }
  catch { return ""; }
}

export function clearSessionToken() { setSessionToken(null); }

export const api = axios.create({
  baseURL: API,
  // On web we NEED cookies (session_token). On Capacitor native we
  // exclusively use Bearer tokens because WKWebView blocks 3rd-party
  // cookies, AND sending `credentials: include` triggers a CORS spec
  // violation on this backend (Access-Control-Allow-Origin: * cannot be
  // combined with Access-Control-Allow-Credentials: true) which would
  // silently fail every request from the mobile app.
  withCredentials: !IS_NATIVE,
});

// Attach Bearer token on every request when we have one. Safe on web —
// cookie + header both work, and duplicate auth doesn't break the server.
api.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  // v147.99.33 — HQ branch impersonation. When an HQ admin has picked
  // a branch to "view as" from the OrgHub overview, we attach the
  // branch id so backend swaps to that branch's data for every read.
  // Backend enforces read-only (mutating verbs get a 403), and only
  // honours the header when the caller is a verified HQ admin.
  try {
    const branchId = window?.localStorage?.getItem("linkola_hq_impersonate_branch");
    if (branchId) {
      config.headers = config.headers || {};
      if (!config.headers["X-Impersonate-Branch"]) {
        config.headers["X-Impersonate-Branch"] = branchId;
      }
    }
  } catch { /* SSR / private-mode safety */ }
  // Advertise the client platform so backend can gate features that
  // must never run inside a native app shell (e.g. plan subscription
  // checkout — App Store / Play Store compliance).
  if (IS_NATIVE) {
    config.headers = config.headers || {};
    if (!config.headers["X-Client-Platform"]) {
      config.headers["X-Client-Platform"] = "native";
    }
  }
  // v146.43 — Advertise the visitor's currency preference on every
  // request so backend endpoints (e.g. /api/plans) can localise
  // pricing without a server-side geo IP lookup. This is a hint only;
  // backend still respects `cf-ipcountry` when present.
  try {
    // eslint-disable-next-line global-require
    const { detectCurrency } = require("./detectCurrency");
    const cur = detectCurrency();
    if (cur) {
      config.headers = config.headers || {};
      if (!config.headers["X-Country-Code"]) {
        config.headers["X-Country-Code"] = cur === "INR" ? "IN" : "US";
      }
    }
  } catch { /* detection is best-effort */ }
  return config;
});

// v149.64 — Identical GETs in flight share one request.
//
// Several components on a screen ask for the same thing at the same moment
// (the coin pill and the notification bell are mounted for the phone header
// AND the desktop sidebar; the app shell, the account switcher and the demo
// banner each read `/me/workspace`; …), so every signed-in page made the
// same 4–6 calls twice — each a full round trip through the origin. While
// one such GET is in flight, another with the same URL, params and headers
// waits for it instead of sending its own.
//
// Nothing is cached: once the request finishes, the next call goes to the
// server as before. Only plain JSON reads are shared — a request with an
// abort signal, a progress callback or a non-JSON `responseType` (file
// downloads), or `dedupe: false`, keeps its own. When a response is shared,
// each caller gets its own copy of `data`, so one can't change what another
// sees.
const _inflightGets = new Map();
const _rawGet = api.get.bind(api);
function _copyResponse(res) {
  let data = res.data;
  try {
    data = typeof window !== "undefined" && typeof window.structuredClone === "function"
      ? window.structuredClone(res.data) : JSON.parse(JSON.stringify(res.data));
  } catch { /* not cloneable — hand over the original */ }
  return { ...res, data };
}
api.get = function dedupedGet(url, config) {
  const c = config || {};
  if (c.dedupe === false || c.signal || c.cancelToken || c.onDownloadProgress
      || (c.responseType && c.responseType !== "json")) {
    return _rawGet(url, config);
  }
  let key;
  try {
    key = `${c.baseURL || ""}|${url}|${JSON.stringify(c.params ?? null)}|${JSON.stringify(c.headers ?? null)}`;
  } catch { return _rawGet(url, config); }
  let entry = _inflightGets.get(key);
  if (entry) {
    entry.sharers += 1;
  } else {
    entry = { sharers: 1, promise: null };
    entry.promise = _rawGet(url, config).finally(() => { _inflightGets.delete(key); });
    _inflightGets.set(key, entry);
  }
  return entry.promise.then((res) => (entry.sharers > 1 ? _copyResponse(res) : res));
};

// Any 401 anywhere in the app means the stored token / cookie is no longer
// trusted by the server — drop it locally so the UI doesn't pretend the
// user is still signed in. Applies globally so no individual caller has to
// remember to handle it.
//
// v141.2 — Also detect the "stale native binary" case: a 404 from a URL
// that contains `.preview.emergentagent.` means this install was built
// against a decommissioned preview pod (users on old App Store / Play
// Store binaries). Surface a helpful update prompt ONCE per session
// instead of leaking the raw axios error.
let _staleBackendToastShown = false;
function _detectStaleNativeBackend(error) {
  if (_staleBackendToastShown) return;
  const url = error?.config?.baseURL || BACKEND_URL || "";
  const badHost = url.includes(".preview.emergentagent.") || url.includes("localhost");
  if (!badHost) return;
  if (!IS_NATIVE) return;
  _staleBackendToastShown = true;
  // Lazy-import to avoid a static circular dep between axios + sonner.
  import("sonner").then(({ toast }) => {
    toast.error(
      "Please update Linkola from the App Store — this build points at a "
      + "server that no longer exists.",
      { duration: 12000 },
    );
  }).catch(() => {});
}

api.interceptors.response.use(
  (r) => r,
  (error) => {
    // v144.6 — Only clear session on TRUE auth failures. A 401 from an
    // API route with an `Authorization` header (or the session cookie)
    // means the token/cookie is stale. A 401 from something completely
    // unrelated (e.g., a 502 that happens to expose an auth hint) does
    // NOT justify silently logging the user out.
    // v147.26 — Tightened further because native-app users were being
    // logged out mid-session by any transient 401 (e.g., a permission
    // check on a background poll, a delayed poll after a route change,
    // or a stale request in-flight during a re-render). Only clear the
    // stored token when EITHER:
    //   • the failing URL is one of the session-critical auth endpoints
    //     (`/auth/me`, `/auth/refresh`, `/auth/logout`), OR
    //   • the response body explicitly says the token is bad
    //     (`invalid_token` / `session_expired` / `token_expired`).
    // Any other 401 (permission denied for a specific record, etc.)
    // stays a 401 — the caller decides what to do — but the local
    // token isn't nuked.
    const status = error?.response?.status;
    if (status === 401) {
      try {
        const url = (error?.config?.url || "").toLowerCase();
        const detail = (
          error?.response?.data?.detail
          || error?.response?.data?.error
          || error?.response?.data?.message
          || ""
        ).toString().toLowerCase();
        const isAuthCritical =
          url.includes("/auth/me")
          || url.includes("/auth/refresh")
          || url.includes("/auth/logout")
          || url.includes("/auth/session");
        const isTokenInvalid =
          detail.includes("invalid_token")
          || detail.includes("invalid token")
          || detail.includes("session_expired")
          || detail.includes("token_expired")
          || detail.includes("token expired");
        if (isAuthCritical || isTokenInvalid) clearSessionToken();
      } catch {
        /* If the sniff itself throws, leave the token alone — safer. */
      }
    }
    if (status === 404) _detectStaleNativeBackend(error);
    if (!error?.response && IS_NATIVE) {
      // Network failure entirely (DNS / TLS / offline) — same fingerprint
      // as a decommissioned preview pod when the OS refuses to connect.
      _detectStaleNativeBackend(error);
    }
    // Attach a friendly message for 5xx / edge failures so callers can
    // surface it without doing `err.response?.data?.detail` gymnastics.
    if (status && status >= 500) {
      const friendly =
        status === 502 || status === 504
          ? "Server is taking too long. Try again in a moment — you are still signed in."
          : "Something went wrong on our end. Try again shortly.";
      error.friendlyMessage = friendly;
    }
    return Promise.reject(error);
  },
);
